import Head from 'next/head';
import PropTypes from 'prop-types';
import { NextSeo } from 'next-seo';
import EnrolledCourse from '@cont/EnrolledCourse/EnrolledCourse';
import {
  fetchEnrolledCourses,
  fetchEnrolledCoursesFilters
} from '@actions/courses';
import { useSetSelectedNav } from '@stores/sidenav';
import { ENROLLMENTS } from '@constants/segment';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { useEffect } from 'react';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { useDispatch } from 'react-redux';
import useEnrolledPageRedirection from '@api/hooks/userEnrollment/useEnrolledPageRedirection';

const EnrolledCoursesPage = ({ goalUID, querySlug, fallbackData }) => {
  const { goalInfo } = fallbackData;
  const { title } = goalInfo || {};
  const metaTitle = `My Enrollments | ${title} | Unacademy`;
  const dispatch = useDispatch();

  useSetSelectedNav('enrolled');
  useEnrolledPageRedirection({ goalUID, querySlug });

  useEffect(() => {
    dispatch(fetchEnrolledCoursesFilters(goalUID));
    dispatch(
      fetchEnrolledCourses(
        goalUID,
        {
          state: 'all',
          language: 'all',
          educator: 'all',
          topic: 'all'
        },
        false
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <NextSeo
        title={metaTitle}
        openGraph={{
          title: metaTitle
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <EnrolledCourse seeAllType="enrolled" />
    </>
  );
};

EnrolledCoursesPage.pageInfo = {
  isMWebSupported: true,
  name: ENROLLMENTS,
  layoutOptions: {
    showPlusNav: true
  }
};

export const getStaticProps = async ({ params }) => {
  const { goalUID: routeGoalUID, goalSlug: querySlug } = params || {};
  if (!routeGoalUID || !querySlug) return { notFound: true };
  const goalUID = routeGoalUID?.toUpperCase();
  try {
    const goalInfo = await fetchGoalInfo(goalUID);
    if (goalInfo?.isPlatformGoal)
      return {
        redirect: {
          destination: `/goal/${querySlug}/${goalUID}/batch-group/home`
        }
      };

    const fallbackData = { goalInfo };

    return {
      props: {
        goalUID,
        querySlug,
        fallbackData,
        globalFallbackData: {
          [getTopologyKey(goalInfo.uid)]: goalInfo
        }
      },
      revalidate: 5 // keeping the revalidate time to 5 seconds as this page is cached over cdn
    };
  } catch {
    return {
      redirect: {
        destination: `/goal/${querySlug}/${goalUID}`,
        permanent: false
      },
      revalidate: 5
    };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};
export default EnrolledCoursesPage;

EnrolledCoursesPage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.any).isRequired
};
EnrolledCoursesPage.Layout = Layout;
EnrolledCoursesPage.Wrapper = PageReduxProvider;
